import type { CorrelationEntry } from './types';
import {getCorrelationColor } from './palettes';

export interface HeatmapOptions {
  showLabels?: boolean;
  maxItems?: number;
}

/**
 * Renders a horizontal bar-based heatmap visualization for correlation data.
 * Shows the main axis correlations with positive values on top and negative on bottom.
 */
export function renderCorrelationHeatmap(
  entry: CorrelationEntry,
  options: HeatmapOptions = {}
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'chart chart--heatmap correlation-heatmap';

  const { maxItems = 10 } = options;

  // Compute global max absolute value across both sections for shared scaling
  const topValues = Object.values(entry.top_data).map(Math.abs);
  const bottomValues = Object.values(entry.bottom_data).map(Math.abs);
  const globalMaxAbsValue = Math.max(...topValues, ...bottomValues, 0.001);

  // Create top (positive) section
  const topSection = createCorrelationSection(
    entry.top_data,
    'Top Positive Correlations',
    'positive',
    maxItems,
    globalMaxAbsValue
  );
  container.appendChild(topSection);

  // Create bottom (negative) section  
  const bottomSection = createCorrelationSection(
    entry.bottom_data,
    'Top Negative Correlations', 
    'negative',
    maxItems,
    globalMaxAbsValue
  );
  container.appendChild(bottomSection);

  return container;
}

function createCorrelationSection(
  data: Record<string, number>,
  title: string,
  type: 'positive' | 'negative',
  maxItems: number,
  globalMaxAbsValue: number
): HTMLElement {
  const section = document.createElement('div');
  section.className = `heatmap-section heatmap-section--${type}`;

  const sectionTitle = document.createElement('h4');
  sectionTitle.className = 'heatmap-section-title';
  sectionTitle.textContent = title;
  section.appendChild(sectionTitle);

  // Sort by absolute correlation value (descending for positive, ascending for negative)
  let entries = Object.entries(data)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, maxItems);
  
  // Reverse negative correlations so weakest are at top, strongest at bottom
  if (type === 'negative') {
    entries = entries.reverse();
  }

  if (entries.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'heatmap-empty';
    empty.textContent = 'No significant correlations';
    section.appendChild(empty);
    return section;
  }

  const barsWrapper = document.createElement('div');
  barsWrapper.className = 'heatmap-bars-wrapper';

  // Calculate position of reference line at |0.2| as percentage
  const refLinePercent = (0.2 / globalMaxAbsValue) * 100;

  // Container for all rows
  const rowsContainer = document.createElement('div');
  rowsContainer.className = 'heatmap-rows';

  // Create rows where each row contains both label and bar
  entries.forEach(([label, value]) => {
    const row = document.createElement('div');
    row.className = 'heatmap-row';

    const labelEl = document.createElement('div');
    labelEl.className = 'heatmap-label';
    labelEl.textContent = label;
    labelEl.title = label;
    row.appendChild(labelEl);

    const barTrack = document.createElement('div');
    barTrack.className = 'heatmap-bar-track';

    const bar = document.createElement('div');
    bar.className = 'heatmap-bar';
    
    // Calculate width as percentage of global max (shared x-axis)
    const widthPercent = (Math.abs(value) / globalMaxAbsValue) * 100;
    bar.style.width = `${widthPercent}%`;
    bar.style.backgroundColor = getCorrelationColor(value);

    const valueEl = document.createElement('span');
    valueEl.className = 'heatmap-value';
    valueEl.textContent = formatCorrelation(value);
    // Position value at end of bar
    valueEl.style.left = `${widthPercent}%`;

    barTrack.appendChild(bar);
    barTrack.appendChild(valueEl);
    row.appendChild(barTrack);
    rowsContainer.appendChild(row);
  });

  barsWrapper.appendChild(rowsContainer);

  // Add reference line container that spans all rows (positioned over bars area)
  if (refLinePercent <= 100) {
    const refLineContainer = document.createElement('div');
    refLineContainer.className = 'heatmap-ref-line-container';
    
    const refLine = document.createElement('div');
    refLine.className = 'heatmap-ref-line';
    refLineContainer.appendChild(refLine);
    
    const refLabel = document.createElement('div');
    refLabel.className = 'heatmap-ref-label';
    if (type === 'negative') {
      refLabel.classList.add('heatmap-ref-label-bottom');
    }
    refLabel.textContent = type === 'positive' ? '+0.2' : '-0.2';
    refLineContainer.appendChild(refLabel);
    
    barsWrapper.appendChild(refLineContainer);

    // Position the reference line after layout using requestAnimationFrame
    const positionRefLine = () => {
      const firstBarTrack = rowsContainer.querySelector('.heatmap-bar-track') as HTMLElement;
      if (firstBarTrack && barsWrapper.offsetParent) {
        const wrapperRect = barsWrapper.getBoundingClientRect();
        const trackRect = firstBarTrack.getBoundingClientRect();
        const trackLeft = trackRect.left - wrapperRect.left;
        const trackWidth = trackRect.width;
        // Round to nearest pixel to avoid sub-pixel rendering artifacts
        const refLineLeft = Math.round(trackLeft + (trackWidth * refLinePercent / 100));
        refLineContainer.style.left = `${refLineLeft}px`;
      }
    };

    // Use ResizeObserver to reposition on layout changes
    const resizeObserver = new ResizeObserver(positionRefLine);
    requestAnimationFrame(() => {
      positionRefLine();
      resizeObserver.observe(barsWrapper);
    });
  }

  section.appendChild(barsWrapper);
  return section;
}

function formatCorrelation(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${(value).toFixed(3)}`;
}
